import React, { ReactNode, useEffect, useRef } from 'react';
import { WithTestId } from '../../types';
import { toKebabCase } from '../../utils/casing';
import styles from './controlGroup.module.css';
import InputHelperText from '../InputHelper';
import { Tooltip } from '../Tooltip/Tooltip';
import Icon from '../Icon';
import { getTextLength } from '../../utils/getTextLength';

export interface ControlGroupProps extends WithTestId {
  children: ReactNode;
  label?: string;
  description?: string;
  optional?: string;
}

const ControlGroup = ({
  children,
  label,
  description,
  optional,
  dataTestIdPrefix,
}: ControlGroupProps) => {
  const descriptionRef = useRef<HTMLFormElement | null>(null);

  const getLabelTextLength = () => {
    if (label) return getTextLength(label);
  };
  const getDescriptionTextLength = () => {
    if (description) return getTextLength(description);
  };

  const getOptionalTextLength = () => {
    if (optional) return getTextLength(optional);
  };

  const totalCharacters =
    +(getLabelTextLength() || 0) +
    +(getDescriptionTextLength() || 0) +
    +(getOptionalTextLength() || 0);

  /*
    will show tooltip if total number of char
    in the control group is >= 41 and
    if screen is <= iPad (by styles)
  */
  const showTooltip = totalCharacters >= 41;

  const descriptionClassName = showTooltip
    ? styles[`controlGroupDescription--withTooltip`]
    : styles[`controlGroupDescription`];

  interface ToolTipContentProps {
    content: string;
  }
  const ToolTipContent = ({ content }: ToolTipContentProps) => (
    <div className={styles.controlGroupDescriptionTooltipContent}>
      {content}
    </div>
  );

  const ToolTipIcon = () => <Icon name="info" size={16} />;

  /*
    passing var --crmis-input-control-group-text-length to
    the styles for a bit more dynamic css
  */
  useEffect(() => {
    if (description && descriptionRef.current) {
      descriptionRef.current.style.setProperty(
        '--crmis-input-control-group-text-length',
        `${totalCharacters}`
      );
    }
  }, [totalCharacters, description]);

  return (
    <div
      className={styles.controlGroup}
      data-testid={toKebabCase(dataTestIdPrefix, 'controlGroup')}
    >
      <div className={styles.controlGroupDescriptors}>
        {label && (
          <InputHelperText
            className={styles.controlGroupLabel}
            dataTestIdPrefix={toKebabCase(
              dataTestIdPrefix,
              'controlGroup',
              'label'
            )}
          >
            {label}
          </InputHelperText>
        )}
        {description && (
          <InputHelperText
            ref={descriptionRef}
            className={descriptionClassName}
            dataTestIdPrefix={toKebabCase(
              dataTestIdPrefix,
              'controlGroup',
              'description'
            )}
          >
            <span className={`${styles.controlGroupDescriptionText}`}>
              {description}
            </span>
            {showTooltip && (
              <Tooltip
                className={`${styles.controlGroupDescriptionTooltip}`}
                toolTipTrigger={<ToolTipIcon />}
                toolTipContent={<ToolTipContent content={description} />}
                dataTestIdPrefix={dataTestIdPrefix}
              />
            )}
          </InputHelperText>
        )}
        {optional && (
          <InputHelperText
            className={styles.controlGroupOption}
            dataTestIdPrefix={toKebabCase(
              dataTestIdPrefix,
              'controlGroup',
              'optional'
            )}
          >
            {optional}
          </InputHelperText>
        )}
      </div>
      <div className={styles.controlGroupInputs}>{children}</div>
    </div>
  );
};

export default ControlGroup;
